import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  advisors,
  FUNCIONALIDADE_ASSESSORIA,
  type Advisor,
  type AdvisorInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatPhone } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { PartyToolbar } from '../shared/PartyToolbar';
import { PERMISSAO_ASSESSORIA_CRIAR, PERMISSAO_ASSESSORIA_EDITAR } from './rotulos';

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'true', label: 'Ativas' },
  { value: 'false', label: 'Inativas' },
];

const limite = 20;

/** Cadastro e edicao da assessoria. Os campos do OnPoint nao foram lidos. */
function AdvisorForm({
  atual,
  enviando,
  erro,
  onCancelar,
  onEnviar,
}: {
  atual: Advisor | null;
  enviando: boolean;
  erro: Error | null;
  onCancelar: () => void;
  onEnviar: (entrada: AdvisorInput) => void;
}) {
  const [nome, setNome] = useState(atual?.name ?? '');
  const [documento, setDocumento] = useState(
    atual?.document ? formatCpfCnpj(atual.document) : '',
  );
  const [email, setEmail] = useState(atual?.email ?? '');
  const [telefone, setTelefone] = useState(atual?.phone ?? '');
  const [observacoes, setObservacoes] = useState(atual?.notes ?? '');
  const erroDaApi = erro instanceof ApiError ? erro : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    onEnviar({
      name: nome,
      document: documento,
      email,
      phone: telefone,
      notes: observacoes,
    });
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-3">
      {erro ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {erro.message}
        </Alert>
      ) : null}
      <Field
        label="Nome da assessoria"
        required
        maxLength={200}
        value={nome}
        onChange={(evento) => setNome(evento.target.value)}
        error={fieldErrorOf(erroDaApi, 'name')}
      />
      <Field
        label="CNPJ da assessoria"
        value={documento}
        onChange={(evento) => setDocumento(evento.target.value)}
        error={fieldErrorOf(erroDaApi, 'document')}
        hint="Opcional. Informado, precisa ser valido e unico na empresa."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="E-mail da assessoria"
          type="email"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
          error={fieldErrorOf(erroDaApi, 'email')}
        />
        <Field
          label="Telefone da assessoria"
          value={telefone}
          onChange={(evento) => setTelefone(evento.target.value)}
          error={fieldErrorOf(erroDaApi, 'phone')}
        />
      </div>
      <Textarea
        label="Observacoes da assessoria"
        maxLength={2000}
        value={observacoes}
        onChange={(evento) => setObservacoes(evento.target.value)}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </Button>
        <Button type="submit" loading={enviando} disabled={nome.trim() === ''}>
          Salvar assessoria
        </Button>
      </div>
    </form>
  );
}

/**
 * Lista de assessorias (P-COR-3). A assessoria do OnPoint reune uma carteira de
 * corretoras, com comissao propria; na Sudaseg o modulo esta marcado "NAO" (CFG-003),
 * e a funcionalidade nasce desligada em toda empresa. O acesso do proprio assessor e
 * do pacote de acesso (P-ACE-2).
 */
export function AdvisorsPage() {
  const { can, hasFeature } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('');
  const [offset, setOffset] = useState(0);
  const [editando, setEditando] = useState<Advisor | 'nova' | null>(null);

  const ligada = hasFeature(FUNCIONALIDADE_ASSESSORIA);
  const podeCriar = can(PERMISSAO_ASSESSORIA_CRIAR);
  const podeEditar = can(PERMISSAO_ASSESSORIA_EDITAR);

  const filtros = { q: busca, active: situacao };
  const consulta = useQuery({
    queryKey: ['advisors', { ...filtros, offset }],
    queryFn: ({ signal }) =>
      advisors.list({ ...filtros, limit: limite, offset }, signal),
    enabled: ligada,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['advisors'] });
    void queryClient.invalidateQueries({ queryKey: ['advisor'] });
  }

  const salvar = useMutation({
    mutationFn: (entrada: AdvisorInput) =>
      editando && editando !== 'nova'
        ? advisors.update(editando.id, entrada)
        : advisors.create(entrada),
    onSuccess: (assessoria) => {
      toast.success(`Assessoria ${assessoria.name} salva.`);
      setEditando(null);
      invalidar();
    },
  });

  const alternar = useMutation({
    mutationFn: (assessoria: Advisor) =>
      advisors.update(assessoria.id, { active: !assessoria.active }),
    onSuccess: (assessoria) => {
      toast.success(
        `Assessoria ${assessoria.name} ${assessoria.active ? 'ativada' : 'desativada'}.`,
      );
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!ligada) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Assessorias" />
        <EmptyState
          icon="users"
          title="Assessoria desligada nesta empresa"
          description="A assessoria nasce desligada, como no sistema anterior da seguradora. A administracao da plataforma liga a funcionalidade quando a empresa trabalhar com assessorias."
        />
      </div>
    );
  }

  const colunas: Column<Advisor>[] = [
    {
      key: 'acoes',
      header: 'Acoes',
      render: (assessoria) => (
        <div className="flex gap-1" onClick={(evento) => evento.stopPropagation()}>
          <AcaoComIcone
            acao="verItens"
            rotulo="Ver corretoras"
            onClick={() => void navigate(`/assessorias/${assessoria.id}/corretores`)}
          />
          {podeEditar ? (
            <>
              <AcaoComIcone
                acao="editar"
                rotulo="Editar"
                onClick={() => {
                  salvar.reset();
                  setEditando(assessoria);
                }}
              />
              <AcaoComIcone
                acao={assessoria.active ? 'desativar' : 'ativar'}
                rotulo={assessoria.active ? 'Desativar' : 'Ativar'}
                loading={alternar.isPending && alternar.variables?.id === assessoria.id}
                onClick={() => alternar.mutate(assessoria)}
              />
            </>
          ) : null}
        </div>
      ),
    },
    {
      key: 'nome',
      header: 'Nome',
      render: (assessoria) => (
        <p className="truncate font-medium text-ink-900">{assessoria.name}</p>
      ),
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      render: (assessoria) =>
        assessoria.document ? (
          <span className="whitespace-nowrap tabular-nums">
            {formatCpfCnpj(assessoria.document)}
          </span>
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'contato',
      header: 'Contato',
      hideBelow: 'md',
      render: (assessoria) =>
        [assessoria.email, assessoria.phone ? formatPhone(assessoria.phone) : '']
          .filter(Boolean)
          .join(' / ') || <span className="text-ink-400">-</span>,
    },
    {
      key: 'corretoras',
      header: 'Corretoras',
      align: 'right',
      render: (assessoria) => assessoria.broker_count,
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (assessoria) =>
        assessoria.active ? (
          <Badge tone="success">Ativa</Badge>
        ) : (
          <Badge tone="neutral">Inativa</Badge>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Assessorias"
        description="Assessorias que reunem corretoras, com a comissao da assessoria sobre cada uma."
        actions={
          podeCriar ? (
            <Button
              onClick={() => {
                salvar.reset();
                setEditando('nova');
              }}
            >
              Nova assessoria
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as assessorias">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por nome ou CNPJ"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              options={situacoes}
              value={situacao}
              onChange={(evento) => {
                setSituacao(evento.target.value);
                setOffset(0);
              }}
            />
          }
        />
        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(assessoria) => assessoria.id}
          loading={consulta.isPending}
          onRowClick={(assessoria) =>
            void navigate(`/assessorias/${assessoria.id}/corretores`)
          }
          empty={
            <EmptyState
              icon="users"
              title="Nenhuma assessoria encontrada"
              description="A assessoria reune corretoras; a comissao da assessoria e registrada em cada uma."
            />
          }
        />
        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={editando !== null}
        onClose={() => setEditando(null)}
        title={
          editando && editando !== 'nova'
            ? `Editar ${editando.name}`
            : 'Nova assessoria'
        }
        size="md"
      >
        {editando !== null ? (
          <AdvisorForm
            key={editando === 'nova' ? 'nova' : editando.id}
            atual={editando === 'nova' ? null : editando}
            enviando={salvar.isPending}
            erro={salvar.error}
            onCancelar={() => setEditando(null)}
            onEnviar={(entrada) => salvar.mutate(entrada)}
          />
        ) : null}
      </Modal>
    </div>
  );
}
